import { watch } from 'vue';

import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import {
  applyCurrencyRounding,
  formattedPrice
} from '@/shared/utils/shopify/money-formatter';

const initBulkQtyPicker = () => {
  const bulkPicker = document.querySelector<HTMLElement>('.js-bulk-qty-picker');
  const defaultSelector = document.getElementById(
    'js-product-quantity-selector'
  );

  if (!bulkPicker) return;

  // Only init if this product actually has bulk discount tiers
  const hasTiers = bulkPicker.querySelectorAll('.iq-tier').length > 0;
  if (!hasTiers) return;

  if (defaultSelector) {
    defaultSelector.classList.add('hidden');
  }

  initPickerLogic(bulkPicker, defaultSelector);
};

function initPickerLogic(
  section: HTMLElement,
  defaultSelector: HTMLElement | null
) {
  let qty = 1;
  const min = 1;
  const max = 9999;

  const valEl = document.getElementById('iq-val') as HTMLInputElement | null;
  const minusBtn = document.getElementById('iq-minus') as HTMLButtonElement;
  const plusBtn = document.getElementById('iq-plus') as HTMLButtonElement;
  const totalEl = document.getElementById('iq-total');
  if (!valEl || !minusBtn || !plusBtn) return;

  const tiers = Array.from(section.querySelectorAll<HTMLElement>('.iq-tier'));
  let basePrice = parseInt(section.dataset.basePrice || '0');
  const defaultQty = parseInt(section.dataset.defaultQty || '1');

  // Localized string passed from Liquid via data attribute
  const tSaveAmount = section.dataset.tSaveAmount || 'Save __AMOUNT__';

  const priceWrapperEl =
    document.querySelector<HTMLElement>('.js-price-wrapper');
  const priceBeforeEl = document.querySelector<HTMLElement>('.js-price-before');
  const bulkBadgeEl = document.querySelector<HTMLElement>('.js-pdp-bulk-badge');

  function getActiveTier(q: number): HTMLElement | null {
    let active: HTMLElement | null = null;
    tiers.forEach((t) => {
      if (q >= parseInt(t.dataset.qty || '0')) active = t;
    });
    return active;
  }

  /** Recalculate unit prices on all tier buttons from discount percentages and current basePrice. */
  function recalcTierUnitPrices() {
    tiers.forEach((t) => {
      const pct = parseFloat(t.dataset.discountPct || '0');
      const savingsCents = Math.floor((basePrice * pct) / 100);
      const newUnitPrice = applyCurrencyRounding(basePrice - savingsCents);
      t.dataset.unitPrice = String(newUnitPrice);
      // Update visible price label
      const priceLabel = t.querySelector<HTMLElement>('.iq-tier-price');
      if (priceLabel) {
        const perUnit = section.dataset.tPerUnit || '';
        priceLabel.textContent = formattedPrice(newUnitPrice) + perUnit;
      }
    });
  }

  function syncStore(n: number) {
    const quantityStore = useProductQuantitiesStore();
    quantityStore.updateQuantity(n);
  }

  function pulseVal() {
    valEl!.classList.remove('iq-val--bump');
    void valEl!.offsetWidth;
    valEl!.classList.add('iq-val--bump');
  }

  function updateTotal(unitPrice: number) {
    if (!totalEl) return;

    const total = unitPrice * qty;
    const saved = (basePrice - unitPrice) * qty;

    // Clear previous content
    totalEl.textContent = '';

    // Build: "= <strong>total</strong> <span>Save X</span>"
    const eqText = document.createTextNode('= ');
    const strongEl = document.createElement('strong');
    strongEl.textContent = formattedPrice(total);
    totalEl.appendChild(eqText);
    totalEl.appendChild(strongEl);

    if (saved > 0) {
      const spaceText = document.createTextNode(' ');
      const savingEl = document.createElement('span');
      savingEl.className = 'iq-saving';
      savingEl.textContent = tSaveAmount.replace(
        '__AMOUNT__',
        formattedPrice(saved)
      );
      totalEl.appendChild(spaceText);
      totalEl.appendChild(savingEl);
    }

    totalEl.classList.remove('iq-total--update');
    void totalEl.offsetWidth;
    totalEl.classList.add('iq-total--update');
  }

  function setQty(n: number, skipPulse?: boolean) {
    qty = Math.max(min, Math.min(max, n));
    valEl!.value = String(qty);
    if (!skipPulse) pulseVal();
    minusBtn.disabled = qty <= min;
    plusBtn.disabled = qty >= max;
    syncStore(qty);

    if (tiers.length === 0) return;

    const active = getActiveTier(qty);
    tiers.forEach((t) => {
      const on = t === active;
      t.classList.toggle('iq-tier--active', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    if (active) {
      const unitPrice = parseInt(active.dataset.unitPrice || String(basePrice));

      updateTotal(unitPrice);

      const isDiscounted = unitPrice < basePrice;
      if (priceWrapperEl) {
        priceWrapperEl.textContent = formattedPrice(unitPrice);
        priceWrapperEl.style.color = isDiscounted ? '#f31c63' : '';
        if (!skipPulse) {
          priceWrapperEl.classList.remove('iq-price--update');
          void priceWrapperEl.offsetWidth;
          priceWrapperEl.classList.add('iq-price--update');
        }
      }
      if (priceBeforeEl) {
        if (isDiscounted) {
          priceBeforeEl.textContent = formattedPrice(basePrice);
          priceBeforeEl.classList.remove('hidden');
        } else {
          priceBeforeEl.classList.add('hidden');
        }
      }
    }
  }

  tiers.forEach((tier) => {
    tier.addEventListener('click', () =>
      setQty(parseInt(tier.dataset.qty || '1'))
    );
  });
  minusBtn.addEventListener('click', () => setQty(qty - 1));
  plusBtn.addEventListener('click', () => setQty(qty + 1));

  // Allow keyboard editing of the quantity input
  valEl.addEventListener('keydown', (e: KeyboardEvent) => {
    // Commit on Enter — delegate to blur handler to avoid double setQty
    if (e.key === 'Enter') {
      valEl.blur();
      e.preventDefault();
      return;
    }
    // Arrow keys step from the current input value (not last committed qty)
    // so typed-but-uncommitted values aren't discarded
    if (e.key === 'ArrowUp') {
      const current = parseInt(valEl.value, 10);
      const base = isNaN(current) ? qty : current;
      setQty(base + 1);
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      const current = parseInt(valEl.value, 10);
      const base = isNaN(current) ? qty : current;
      setQty(base - 1);
      e.preventDefault();
      return;
    }
    // Block non-numeric keys (allow navigation/editing keys)
    const allowedKeys = [
      'Tab',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End'
    ];
    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (isNaN(parseInt(e.key))) e.preventDefault();
  });

  valEl.addEventListener('blur', () => {
    const parsed = parseInt(valEl.value, 10);
    setQty(isNaN(parsed) ? min : parsed, true);
  });

  // Select all text on focus for easy replacement
  valEl.addEventListener('focus', () => {
    valEl.select();
  });

  setQty(defaultQty, true);

  syncStore(qty);

  watch(
    () => useProductQuantitiesStore().quantity,
    (storeQty) => {
      if (storeQty === qty) return;
      if (section.classList.contains('hidden')) {
        qty = storeQty;
        return;
      }
      setQty(storeQty);
    }
  );

  // Listen for variant changes from Vue options app (uses replaceState)
  const origReplace = history.replaceState;
  history.replaceState = function (...args) {
    origReplace.apply(history, args);
    // Defer so activeOption is updated before we read it
    setTimeout(refreshBasePrice, 0);
  };
  window.addEventListener('popstate', refreshBasePrice);

  function refreshBasePrice() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant');
    if (!variantId) return;
    try {
      const s = window.hobbiiStore?.state?.value;
      if (s?.['product-options']?.activeOption) {
        const variant = s['product-options'].activeOption;
        const rawPrice = variant.price;
        const compareAtPrice = variant.compare_at_price;

        // Hide bulk picker for on-sale variants (matches Liquid guard)
        // Sticky bar is inside the section, so hiding section hides it too
        // Use raw (unrounded) price so rounding can't close the gap between
        // price and compare_at_price and hide the sale (matches Liquid guard).
        const isOnSale = compareAtPrice && compareAtPrice > rawPrice;
        bulkBadgeEl?.classList.toggle('hidden', Boolean(isOnSale));
        if (isOnSale) {
          section.classList.add('hidden');
          if (defaultSelector) defaultSelector.classList.remove('hidden');
          return;
        }

        // Restore bulk picker if variant is not on sale
        section.classList.remove('hidden');
        if (defaultSelector) defaultSelector.classList.add('hidden');

        // Round for basePrice so data-unit-price math stays on the pretty-price grid
        const newPrice = applyCurrencyRounding(rawPrice);
        if (newPrice && newPrice !== basePrice) {
          basePrice = newPrice;
          recalcTierUnitPrices();
        }
        // Always re-apply tier pricing to refresh the price display
        setQty(qty, true);
      }
    } catch {
      // silently fail
    }
  }
}

initBulkQtyPicker();
